import {analyticsLogEvent} from '../api'

type LayerAction = 'open' | 'close'

interface Parameters {
    /** Interaction with the layer: open on first display, close on dismiss. */
    action: LayerAction
    /** True when the layer is opened from the desktop sticky bar (vs. inline PDP). */
    fromStickybar?: boolean
    /** True while the image zoom modal is open (sticky bar stays visible over the zoom on desktop). */
    isZoomOpen?: boolean
}

/**
 * @name analyticsEventPdpColorLayer
 *
 * @event
 *
 * @description
 * Tracks the PDP color-selector layer open/close (the drawer/popover opened from the
 * "+ N colori" CTA, both inline and from the stickybar).
 * Manuale BitBang "GTM Tracciamenti – PDP PWA" v1.0 §3.1.4.
 *
 * dataLayer shape:
 * `{ event: 'custom_event', eventCategory: 'pdp_color_layer', eventAction: 'select color', eventLabel: 'open'|'close' }`
 *
 * @param {Object} params - Object containing the event parameters.
 * @param {LayerAction} params.action - open/close, mapped to `eventLabel`.
 * @param {boolean} [params.fromStickybar] - True when opened from the desktop sticky bar.
 * @param {boolean} [params.isZoomOpen] - True while the image zoom modal is open.
 */
export function analyticsEventPdpColorLayer({action, fromStickybar = false, isZoomOpen = false}: Parameters) {
    analyticsLogEvent('custom_event', {
        eventCategory: 'pdp_color_layer',
        eventAction: 'select color',
        eventLabel: action,
        // accessType must be symmetric across open / close / select
        accessType: fromStickybar ? (isZoomOpen ? 'pdp-sticky-bottom-zoom' : 'pdp-sticky-bottom') : 'pdp',
    })
}

/**
 * @name analyticsEventPdpColorLayerSelect
 *
 * @event
 *
 * @description
 * Tracks the actual color selection (click on a color swatch that changes the active variation group),
 * complementing the open/close events above.
 * Manuale BitBang "GTM Tracciamenti – PDP PWA" v1.0 §3.1.4.
 *
 * dataLayer shape:
 * `{ event: 'custom_event', eventCategory: 'pdp_color_layer', eventAction: 'select color', eventLabel: 'click' }`
 *
 * @param {Object} [params] - Object containing the event parameters.
 * @param {boolean} [params.fromStickybar] - True when selected from the desktop sticky bar.
 * @param {boolean} [params.isZoomOpen] - True while the image zoom modal is open.
 */
export function analyticsEventPdpColorLayerSelect({fromStickybar = false, isZoomOpen = false}: Omit<Parameters, 'action'> = {}) {
    analyticsLogEvent('custom_event', {
        eventCategory: 'pdp_color_layer',
        eventAction: 'select color',
        eventLabel: 'click',
        // accessType must be symmetric across open / close / select
        accessType: fromStickybar ? (isZoomOpen ? 'pdp-sticky-bottom-zoom' : 'pdp-sticky-bottom') : 'pdp',
    })
}
